/**
 * Gefahrstoffverzeichnis — Owner-Leseansicht (Aug 2026).
 *
 * Rechtsbezug § 6 GefStoffV. Vorbild: Handschutzplan.jsx (Karten-Liste,
 * 403 → „nicht freigeschaltet", Server-Gate). Standardansicht Zero-Noise:
 * Produkt, Hersteller, Signalwort + GHS, Einsatzbereiche, Mengenklasse.
 * Details je Eintrag per handgebautem Toggle (Chevron/aria-expanded,
 * Muster MonteurHandschutzplan.jsx). Reine Leseansicht.
 */
import { useEffect, useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  ExternalLink,
  FileDown,
  Filter,
  Loader2,
  Search,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { formatDateDe } from "@/lib/handschutzplan";

// Anzeige-Labels (Codes kommen aus dem Backend-Vokabular hazmat_registry.py).
const FREQUENCY_LABELS = {
  selten: "Selten (wenige Male im Jahr)",
  gelegentlich: "Gelegentlich (mehrmals im Monat)",
  regelmaessig: "Regelmäßig (wöchentlich)",
  taeglich: "Täglich (mehrmals täglich)",
};

const PSA_LABELS = {
  handschutz: "Handschutz",
  atemschutz: "Atemschutz",
  augenschutz: "Augenschutz",
  koerperschutz: "Körperschutz",
  hautkontakt_erste_hilfe: "Erste Hilfe nach Hautkontakt",
  aetzwirkung_reizung: "Ätz-/Reizwirkung",
  sensibilisierung: "Sensibilisierung",
  p_saetze: "P-Sätze",
};

// Suche + Einsatzbereich-Filter (30.08.2026) · rein client-seitig.
// Pure, separat unit-getestete Filter-Funktion — Muster:
// `filterFskRecordsByName` (UnternehmerFuehrerschein.jsx). Sentinels statt
// roher Namen, damit ein Einsatzbereich namens „Alle" nicht kollidiert.
export const EB_FILTER_ALLE = "__alle__";
export const EB_FILTER_OHNE = "__ohne__";

export function filterGefahrstoffEintraege(eintraege, searchTerm, ebFilter) {
  const term = (searchTerm || "").trim().toLowerCase();
  return (eintraege || []).filter((e) => {
    const namen = e.einsatzbereich_names || [];
    if (ebFilter === EB_FILTER_OHNE && namen.length > 0) return false;
    if (
      ebFilter &&
      ebFilter !== EB_FILTER_ALLE &&
      ebFilter !== EB_FILTER_OHNE &&
      !namen.includes(ebFilter)
    ) {
      return false;
    }
    if (!term) return true;
    return [e.product_name, e.product_manufacturer].some((v) =>
      (v || "").toLowerCase().includes(term)
    );
  });
}

export function SignalwortBadge({ signalwort }) {
  if (!signalwort) return null;
  const istGefahr = signalwort.trim().toLowerCase() === "gefahr";
  return (
    <span
      className={`inline-flex items-center rounded-full text-[11px] font-semibold px-2 py-0.5 ${
        istGefahr ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"
      }`}
      data-testid="gsv-signalwort-badge"
    >
      {signalwort}
    </span>
  );
}

export function GhsBadges({ codes }) {
  if (!codes) return null;
  const liste = codes
    .split(",")
    .map((c) => c.trim())
    .filter(Boolean);
  if (liste.length === 0) return null;
  return (
    <span className="inline-flex flex-wrap gap-1" data-testid="gsv-ghs-badges">
      {liste.map((code) => (
        <span
          key={code}
          className="inline-flex items-center rounded border border-slate-300 bg-slate-50 text-slate-700 text-[11px] font-mono px-1.5 py-0.5"
        >
          {code}
        </span>
      ))}
    </span>
  );
}

function DetailZeile({ label, wert }) {
  if (!wert) return null;
  return (
    <div className="text-sm">
      <span className="font-medium text-slate-700">{label}: </span>
      <span className="text-slate-600">{wert}</span>
    </div>
  );
}

function EintragDetails({ eintrag }) {
  const c = eintrag.classification || {};
  const psa = eintrag.psa || {};
  const substitutionText = eintrag.substitution_checked
    ? `Geprüft${eintrag.substitution_free_text ? ` — ersetzt durch: ${eintrag.substitution_free_text}` : ""}`
    : [eintrag.substitution_reason_label, eintrag.substitution_free_text]
        .filter(Boolean)
        .join(" — ") || "Nicht geprüft";
  const erfasstAm = eintrag.owner_declared_at || eintrag.created_at;
  return (
    <div className="border-t border-slate-100 px-4 py-3 space-y-2">
      <DetailZeile label="Einstufung" wert={c.einstufung_ghs} />
      <DetailZeile label="H-Sätze" wert={c.h_saetze} />
      {Object.entries(PSA_LABELS).map(([key, label]) => (
        <DetailZeile key={key} label={label} wert={psa[key]} />
      ))}
      <DetailZeile
        label="Einsatzhäufigkeit"
        wert={FREQUENCY_LABELS[eintrag.frequency] || eintrag.frequency}
      />
      <div className="text-sm" data-testid="gsv-substitution">
        <span className="font-medium text-slate-700">Substitutionsprüfung: </span>
        <span className="text-slate-600">{substitutionText}</span>
      </div>
      {eintrag.sdb_document_number && (
        <div className="text-sm" data-testid="gsv-sdb-verweis">
          <span className="font-medium text-slate-700">Sicherheitsdatenblatt: </span>
          <span className="text-slate-600 font-mono text-xs">
            {eintrag.sdb_document_number}
          </span>
          {eintrag.sdb_source_url && (
            <a
              href={eintrag.sdb_source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2 text-blue-600 hover:underline inline-flex items-center gap-1 text-xs"
              data-testid="gsv-sdb-quelle-link"
            >
              Quelle ansehen
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      )}
      {erfasstAm && (
        <DetailZeile label="Erfasst am" wert={formatDateDe(erfasstAm)} />
      )}
    </div>
  );
}

export default function Gefahrstoffverzeichnis() {
  const [eintraege, setEintraege] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [offen, setOffen] = useState({});
  const [ladePdf, setLadePdf] = useState(false);
  const [pdfFehler, setPdfFehler] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [ebFilter, setEbFilter] = useState(EB_FILTER_ALLE);

  // Toggle-Reset bei Such-/Filteränderung: der Details-Toggle ist
  // index-basiert — ohne Reset „springt" ein offenes Detail nach dem
  // Filtern auf einen anderen Eintrag (Forensik-Befund 30.08.2026).
  const updateSearch = (value) => {
    setSearchTerm(value);
    setOffen({});
  };
  const updateEbFilter = (key) => {
    setEbFilter(key);
    setOffen({});
  };
  const resetFilter = () => {
    setSearchTerm("");
    setEbFilter(EB_FILTER_ALLE);
    setOffen({});
  };

  // Chip-Optionen dynamisch aus den geladenen Einträgen. Ein Eintrag mit
  // mehreren Einsatzbereichen zählt in mehreren Chips; Einträge OHNE
  // Einsatzbereich bekommen einen eigenen Chip (Realdaten-Befund LUMPE:
  // 9 von 19 Einträgen ohne Zuordnung — kein Randfall).
  const ebOptionen = useMemo(() => {
    const namen = new Map();
    let ohne = 0;
    (eintraege || []).forEach((e) => {
      const ns = e.einsatzbereich_names || [];
      if (ns.length === 0) ohne += 1;
      ns.forEach((n) => namen.set(n, (namen.get(n) || 0) + 1));
    });
    return {
      namen: [...namen.entries()].sort((a, b) =>
        a[0].localeCompare(b[0], "de")
      ),
      ohne,
    };
  }, [eintraege]);

  const gefiltert = useMemo(
    () => filterGefahrstoffEintraege(eintraege || [], searchTerm, ebFilter),
    [eintraege, searchTerm, ebFilter]
  );

  // Register-PDF (30.08.2026): Live-Ableitung, identisch zum ZIP-Export.
  // Blob-Muster wie „Beleg öffnen" (Pruefmappe.jsx), hier als echter
  // Download via <a download> statt window.open.
  const handleDownloadRegisterPdf = async () => {
    setLadePdf(true);
    setPdfFehler("");
    try {
      const { blob } = await api.fetchGefahrstoffRegisterPdfBlob();
      const url = URL.createObjectURL(
        new Blob([blob], { type: "application/pdf" })
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = "Gefahrstoffverzeichnis.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setPdfFehler(
        "Das Verzeichnis-PDF konnte nicht erstellt werden. Bitte versuchen Sie es erneut."
      );
    } finally {
      setLadePdf(false);
    }
  };

  useEffect(() => {
    api
      .listGefahrstoffverzeichnis()
      .then((rows) => setEintraege(Array.isArray(rows) ? rows : []))
      .catch((err) => {
        if (err?.response?.status === 403) setForbidden(true);
        else setErrorMsg("Das Gefahrstoffverzeichnis konnte nicht geladen werden.");
      });
  }, []);

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="gsv-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500 font-medium">
            Gefahrstoff-Modul
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-2">
            Gefahrstoffverzeichnis
          </h1>
          <p className="text-slate-600 mt-2">
            Verzeichnis der Gefahrstoffe in Ihrem Betrieb nach § 6 Abs. 12
            GefStoffV — automatisch gepflegt aus Ihren Gefahrstoff-Welten.
          </p>
        </div>
        {Array.isArray(eintraege) && eintraege.length > 0 && (
          <button
            type="button"
            onClick={handleDownloadRegisterPdf}
            disabled={ladePdf}
            className="inline-flex items-center gap-2 rounded-md bg-slate-900 text-white text-sm font-medium px-4 py-2 hover:bg-slate-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed mt-2"
            data-testid="gsv-register-pdf-download-btn"
          >
            {ladePdf ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileDown className="w-4 h-4" />
            )}
            {ladePdf ? "PDF wird erstellt…" : "Verzeichnis als PDF"}
          </button>
        )}
      </div>

      {pdfFehler && (
        <div
          className="border border-red-200 bg-red-50 text-red-900 rounded-md p-3 text-sm"
          data-testid="gsv-register-pdf-fehler"
        >
          {pdfFehler}
        </div>
      )}

      {forbidden ? (
        <div
          className="border border-amber-200 bg-amber-50 text-amber-900 rounded-md p-4 text-sm"
          data-testid="gsv-forbidden"
        >
          Das Gefahrstoff-Modul ist für Ihren Betrieb nicht freigeschaltet.
        </div>
      ) : errorMsg ? (
        <div
          className="border border-red-200 bg-red-50 text-red-900 rounded-md p-4 text-sm"
          data-testid="gsv-error"
        >
          {errorMsg}
        </div>
      ) : eintraege === null ? (
        <div className="text-slate-500 text-sm" data-testid="gsv-loading">
          Lade…
        </div>
      ) : eintraege.length === 0 ? (
        <div
          className="border border-slate-200 bg-white rounded-md p-6 text-sm text-slate-600"
          data-testid="gsv-empty"
        >
          Es sind noch keine Gefahrstoffe im Verzeichnis erfasst. Einträge
          entstehen automatisch beim Anlegen einer Gefahrstoff-Welt.
        </div>
      ) : (
        <>
          {/* Suche + Filter — Muster: Mitarbeiter.jsx (Suchfeld + Chips) */}
          <div
            className="bg-white border border-slate-200 rounded-lg shadow-sm p-3 space-y-3"
            data-testid="gsv-filter-card"
          >
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input
                value={searchTerm}
                onChange={(e) => updateSearch(e.target.value)}
                placeholder="Produkt oder Hersteller suchen…"
                className="pl-9"
                data-testid="gsv-search-input"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Filter className="w-4 h-4 text-slate-500 ml-2 shrink-0" />
              <div className="flex gap-1 flex-wrap">
                {[
                  {
                    key: EB_FILTER_ALLE,
                    label: "Alle",
                    count: eintraege.length,
                    testid: "gsv-eb-chip-alle",
                  },
                  ...(ebOptionen.ohne > 0
                    ? [{
                        key: EB_FILTER_OHNE,
                        label: "Ohne Einsatzbereich",
                        count: ebOptionen.ohne,
                        testid: "gsv-eb-chip-ohne",
                      }]
                    : []),
                  ...ebOptionen.namen.map(([name, count], ni) => ({
                    key: name,
                    label: name,
                    count,
                    testid: `gsv-eb-chip-${ni}`,
                  })),
                ].map((chip) => {
                  const active = ebFilter === chip.key;
                  return (
                    <button
                      key={chip.key}
                      type="button"
                      onClick={() => updateEbFilter(chip.key)}
                      data-testid={chip.testid}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors inline-flex items-center gap-2 ${
                        active
                          ? "bg-slate-900 text-white"
                          : "text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      {chip.label}
                      <span
                        className={`text-xs px-1.5 py-0.5 rounded ${
                          active
                            ? "bg-white/10"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {chip.count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {gefiltert.length === 0 ? (
            <div
              className="border border-slate-200 bg-white rounded-md p-6 text-sm text-slate-600 space-y-3"
              data-testid="gsv-keine-treffer"
            >
              <p>
                Keine Treffer für die aktuelle Suche bzw. den gewählten
                Einsatzbereich.
              </p>
              <button
                type="button"
                onClick={resetFilter}
                className="text-sm font-medium text-blue-600 hover:underline"
                data-testid="gsv-filter-reset"
              >
                Suche &amp; Filter zurücksetzen
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {gefiltert.map((eintrag, i) => {
            const istOffen = !!offen[i];
            return (
              <div
                key={`${eintrag.product_name}-${i}`}
                className="bg-white border border-slate-200 rounded-lg overflow-hidden"
                data-testid={`gsv-row-${i}`}
              >
                <div className="p-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading font-semibold text-slate-900">
                      {eintrag.product_name}
                    </span>
                    {eintrag.product_manufacturer && (
                      <span className="text-sm text-slate-500">
                        {eintrag.product_manufacturer}
                      </span>
                    )}
                    <SignalwortBadge
                      signalwort={eintrag.classification?.signalwort}
                    />
                    <GhsBadges
                      codes={eintrag.classification?.ghs_piktogramme}
                    />
                  </div>
                  <div className="text-sm text-slate-600 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                    {(eintrag.einsatzbereich_names || []).length > 0 && (
                      <span data-testid={`gsv-eb-namen-${i}`}>
                        {eintrag.einsatzbereich_names.join(", ")}
                      </span>
                    )}
                    {eintrag.quantity_tier_label && (
                      <span data-testid={`gsv-menge-${i}`}>
                        Menge: {eintrag.quantity_tier_label}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setOffen((prev) => ({ ...prev, [i]: !prev[i] }))
                  }
                  className="w-full flex items-center justify-between px-4 py-2 text-xs font-medium text-slate-600 bg-slate-50 hover:bg-slate-100 transition-colors border-t border-slate-100"
                  data-testid={`gsv-toggle-${i}`}
                  aria-expanded={istOffen}
                >
                  <span>{istOffen ? "Details schließen" : "Details anzeigen"}</span>
                  {istOffen ? (
                    <ChevronUp className="w-4 h-4 text-slate-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  )}
                </button>
                {istOffen && (
                  <div data-testid={`gsv-details-${i}`}>
                    <EintragDetails eintrag={eintrag} />
                  </div>
                )}
              </div>
            );
          })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
